/**
 * Support Chat Settings (Superadmin)
 *
 * Configure the support chat widget: select which Slack channel to use,
 * enable/disable the feature, and test the connection.
 *
 * @module
 * @see apps/api/src/superadmin/routes.ts - Backend routes
 */

import React from 'react';
import { Flex, Box, Heading, Text, Card, CardHeader, CardContent, Button, Select, LoadingOrbit, StatusIndicator, type SelectOption } from '@chatsdet/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, PaperPlaneTilt } from '@phosphor-icons/react';
import { API_URL } from '../../../config';

// ============================================================================
// Types
// ============================================================================

interface SupportSettings {
  enabled: boolean;
  channelId: string | null;
  channelName: string | null;
  connected: boolean;
  internalOrgId: string | null;
  internalOrgName: string | null;
}

interface SlackChannel {
  id: string;
  name: string;
}

interface SupportSettingsResponse {
  ok: boolean;
  data: SupportSettings;
}

interface ChannelsResponse {
  ok: boolean;
  data: SlackChannel[];
  error?: string;
}

interface TestResponse {
  ok: boolean;
  data?: { messageTs: string };
  error?: string;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchSupportSettings(): Promise<SupportSettingsResponse> {
  const res = await fetch(`${API_URL}/superadmin/support/settings`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch support settings');
  return res.json();
}

async function fetchChannels(): Promise<ChannelsResponse> {
  const res = await fetch(`${API_URL}/superadmin/support/channels`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to fetch channels');
  }
  return res.json();
}

async function updateSettings(settings: {
  channelId?: string;
  channelName?: string;
  enabled?: boolean;
}): Promise<SupportSettingsResponse> {
  const res = await fetch(`${API_URL}/superadmin/support/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error('Failed to update settings');
  return res.json();
}

async function sendTestMessage(): Promise<TestResponse> {
  const res = await fetch(`${API_URL}/superadmin/support/test`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to send test message');
  }
  return res.json();
}

// ============================================================================
// Component
// ============================================================================

export function SuperadminSupport() {
  const queryClient = useQueryClient();

  // Queries
  const settingsQuery = useQuery({
    queryKey: ['superadmin', 'support', 'settings'],
    queryFn: fetchSupportSettings,
  });

  const channelsQuery = useQuery({
    queryKey: ['superadmin', 'support', 'channels'],
    queryFn: fetchChannels,
    enabled: !!settingsQuery.data?.data.connected,
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'support', 'settings'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: sendTestMessage,
  });

  // Local state
  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(null);

  // Sync local state with fetched settings
  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setSelectedChannelId(settingsQuery.data.data.channelId);
    }
  }, [settingsQuery.data]);

  const settings = settingsQuery.data?.data;
  const channels = channelsQuery.data?.data ?? [];

  const handleChannelChange = async (channelId: string) => {
    setSelectedChannelId(channelId);
    const channel = channels.find((ch) => ch.id === channelId);
    await updateMutation.mutateAsync({ channelId, channelName: channel?.name });
  };

  const handleToggleEnabled = async () => {
    if (!settings) return;
    await updateMutation.mutateAsync({ enabled: !settings.enabled });
  };

  const handleTestMessage = () => {
    testMutation.mutate();
  };

  if (settingsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-20">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card padding="lg">
        <CardContent>
          <Text className="text-destructive">
            {settingsQuery.error instanceof Error
              ? settingsQuery.error.message
              : 'Failed to load support settings'}
          </Text>
        </CardContent>
      </Card>
    );
  }

  const isConfigured = !!settings?.connected && !!settings?.channelId;

  return (
    <Box className="space-y-6">
      {/* Status Card */}
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Support Chat Status</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Enable or disable the support chat widget for all users.
              </Text>
            </div>
            <Flex align="center" gap="md">
              <StatusIndicator
                variant={settings?.enabled ? 'positive' : 'neutral'}
              >
                {settings?.enabled ? 'Enabled' : 'Disabled'}
              </StatusIndicator>
              <Button
                variant={settings?.enabled ? 'outline' : 'primary'}
                onPress={handleToggleEnabled}
                isDisabled={updateMutation.isPending || !isConfigured}
              >
                {settings?.enabled ? 'Disable' : 'Enable'}
              </Button>
            </Flex>
          </Flex>
        </CardHeader>
        <CardContent>
          {!isConfigured && (
            <Text size="sm" className="text-amber-600">
              Configure the internal org Slack integration below before enabling support chat.
            </Text>
          )}
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Slack Configuration</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Support chat uses the internal org Slack workspace configured by INTERNAL_ORG_ID.
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="lg">
            <Box className="max-w-md">
              <Text size="xs" className="text-muted-foreground mb-1.5 block font-medium">
                Internal Organization
              </Text>
              <Text size="sm">
                {settings?.internalOrgName ??
                  settings?.internalOrgId ??
                  'Not configured (set INTERNAL_ORG_ID)'}
              </Text>
              {!settings?.connected && (
                <Text size="sm" className="text-amber-600 mt-2">
                  No Slack connection found for the internal org.
                </Text>
              )}
            </Box>

            {/* Channel Selector */}
            {settings?.connected && (
              <Box className="max-w-md">
                <Text size="xs" className="text-muted-foreground mb-1.5 block font-medium">
                  Support Channel
                </Text>
                {channelsQuery.isLoading ? (
                  <LoadingOrbit size="sm" />
                ) : channelsQuery.isError ? (
                  <Text size="sm" className="text-destructive">
                    {channelsQuery.error instanceof Error
                      ? channelsQuery.error.message
                      : 'Failed to load channels'}
                  </Text>
                ) : channels.length === 0 ? (
                  <Text size="sm" className="text-muted-foreground">
                    No channels available. Make sure the bot is added to channels.
                  </Text>
                ) : (
                  <Select
                    items={channels.map((ch) => ({
                      id: ch.id,
                      label: `#${ch.name}`,
                    })) satisfies SelectOption[]}
                    selectedKey={selectedChannelId ?? undefined}
                    onSelectionChange={(value) => handleChannelChange(value as string)}
                    isDisabled={updateMutation.isPending}
                    placeholder="Select a channel..."
                  />
                )}
              </Box>
            )}

            {/* Current Config Summary */}
            {settings && (
              <Box className="border-t border-border pt-4">
                <Text size="xs" className="text-muted-foreground mb-2 block font-medium">
                  Current Configuration
                </Text>
                <Flex direction="column" gap="xs">
                  <Flex align="center" gap="sm">
                    {settings.connected ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <X size={14} className="text-muted-foreground" />
                    )}
                    <Text size="sm">
                      Internal Org: {settings.internalOrgName ?? settings.internalOrgId ?? 'Not configured'}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="sm">
                    {settings.channelId ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <X size={14} className="text-muted-foreground" />
                    )}
                    <Text size="sm">
                      Channel: {settings.channelName ? `#${settings.channelName}` : settings.channelId ?? 'Not set'}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Flex>
        </CardContent>
      </Card>

      {/* Test Card */}
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Test Connection</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Send a test message to verify the Slack integration is working.
              </Text>
            </div>
            <Button
              variant="outline"
              onPress={handleTestMessage}
              isDisabled={testMutation.isPending || !isConfigured}
            >
              <PaperPlaneTilt size={16} className="mr-2" />
              {testMutation.isPending ? 'Sending...' : 'Send Test Message'}
            </Button>
          </Flex>
        </CardHeader>
        <CardContent>
          {testMutation.isSuccess && (
            <Text size="sm" className="text-green-600">
              Test message sent successfully. Check the configured Slack channel.
            </Text>
          )}
          {testMutation.isError && (
            <Text size="sm" className="text-destructive">
              {testMutation.error instanceof Error
                ? testMutation.error.message
                : 'Failed to send test message'}
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
